import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { TokenSource } from '@/lib/api'
import { safeStorage } from '@/lib/storage'
import type { TokenPair } from '@/types/api.types'
import type { UserSession } from '@/types/models'

// Tokens persist across reloads (localStorage); the user is refetched from GET /auth/me at boot and never persisted, so a role switch
// or profile edit elsewhere is picked up on the next load. Other server data belongs in TanStack Query.
interface SessionState {
  accessToken: string | null
  refreshToken: string | null
  user: UserSession | null
  /** True once a token pair has been stored, before /auth/me confirms it. */
  hasTokens: () => boolean
  setTokens: (tokens: TokenPair) => void
  setUser: (user: UserSession | null) => void
  /** After a role switch: new token pair from PUT /profile, then the caller re-runs the boot. */
  replaceSession: (tokens: TokenPair, user: UserSession | null) => void
  signOut: () => void
  /** Set when the backend rejected the refresh token; the login page shows it once and clears it. */
  sessionExpired: boolean
  markSessionExpired: () => void
  clearSessionExpired: () => void
}

export const SESSION_STORAGE_KEY = 'leazepro.session'

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      hasTokens: () => get().accessToken !== null,
      setTokens: ({ accessToken, refreshToken }) => set({ accessToken, refreshToken }),
      setUser: (user) => set({ user }),
      replaceSession: ({ accessToken, refreshToken }, user) => set({ accessToken, refreshToken, user }),
      signOut: () => set({ accessToken: null, refreshToken: null, user: null }),
      sessionExpired: false,
      markSessionExpired: () => set({ accessToken: null, refreshToken: null, user: null, sessionExpired: true }),
      clearSessionExpired: () => set({ sessionExpired: false }),
    }),
    {
      name: SESSION_STORAGE_KEY,
      storage: createJSONStorage(() => safeStorage),
      partialize: (state) => ({ accessToken: state.accessToken, refreshToken: state.refreshToken }),
    },
  ),
)

/** Adapter the HTTP client reads tokens through, so `lib/api.ts` stays store-agnostic. */
export const sessionTokenSource: TokenSource = {
  getAccessToken: () => useSessionStore.getState().accessToken,
  getRefreshToken: () => useSessionStore.getState().refreshToken,
  setTokens: (tokens) => useSessionStore.getState().setTokens(tokens),
  clear: () => useSessionStore.getState().signOut(),
}
